import { ChangeDetectionStrategy, Component, effect, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Product, ProductType, ProductTypeOption } from '../../common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { IconComponent } from '../../../../lib';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'ca-product-filters',
    templateUrl: './product-filters.component.html',
    styleUrl: './product-filters.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatSelect,
        MatOption,
        MatButton,
        MatIconButton,
        IconComponent,
        TranslatePipe
    ]
})
export class ProductFiltersComponent {
    productName = input<string>();
    productType = input<Product['productType']>();
    allowedProductTypes = input<Product['productType'][]>();
    productNameControl: FormControl = new FormControl();
    productTypeControl: FormControl = new FormControl();

    setFilters = effect(() => {
        const productName = this.productName();
        const productType = this.productType() || this.allowedProductTypes()?.[0];
        this.productNameControl.setValue(productName);
        this.productTypeControl.setValue(productType);
        this.applyFilters();
    });

    /**
     * All currently supported product types for filtering for product picker
     */
    private readonly allSupportedProductTypes: Product['productType'][] = [
        ProductType.DHWCylinder,
        ProductType.GasBoiler,
        ProductType.HeatingOilBoiler,
        ProductType.ElectricHeater,
        ProductType.AirHeatPump,
        ProductType.WaterHeatPump,
        ProductType.HybridAirHeatPump,
        ProductType.GroundHeatPump
    ];

    private readonly allProductTypeOptions: ProductTypeOption[] = this.allSupportedProductTypes.map(value => ({
        value,
        label: `CA.DEVICE_TYPE.${value}`
    })) as ProductTypeOption[];

    get productTypeOptions(): ProductTypeOption[] {
        const allowed = this.allowedProductTypes();
        if (!allowed?.length) return this.allProductTypeOptions;
        const allowedSet = new Set(allowed);
        return this.allProductTypeOptions.filter(opt => allowedSet.has(opt.value));
    }

    ensureAllowedSelection = effect(() => {
        const allowed = this.allowedProductTypes();
        if (!allowed?.length) return;
        const current = this.productTypeControl.value as Product['productType'] | undefined;
        if (current && !allowed.includes(current)) {
            this.productTypeControl.setValue('' as any);
        }
    });

    filter = output<{ productName: string; productType: Product['productType'] }>();

    applyFilters(): void {
        this.filter.emit(this.filterValue);
    }

    private get filterValue(): { productName: string; productType: Product['productType'] } {
        return {
            productName: this.productNameControl.value,
            productType: this.productTypeControl.value
        };
    }
}
